import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc.js';
import timezone from 'dayjs/plugin/timezone.js';
import relativeTime from 'dayjs/plugin/relativeTime.js';
import 'dayjs/locale/pt-br.js';
import { useAuth } from '../contexts/AuthContext.js';
import { authService } from '../services/authService.js';
import { entityService } from '../services/entityService.js';
import { scheduleService } from '../services/scheduleService.js';
import { agendaService } from '../services/agendaService.js';
import { photoService } from '../services/photoService.js';
import { reviewService } from '../services/reviewService.js';
import { notificationService } from '../services/notificationService.js';
import { clinicService } from '../services/clinicService.js';
import { medicalRecordService } from '../services/medicalRecordService.js';
import { analyticsEvents } from '../config/analyticsConfig.js';
import { ReactComponent as Logo } from '../icons/logo_letter.svg';
import { ReactComponent as LogoDark } from '../icons/logo_dark.svg';
import { formatPhone, getDocumentType, formatDocument, SPECIES_LABEL } from '../utils/formatters.js';
import { usePageTitle } from '../hooks/usePageTitle.js';
import placeholderImage from '../images/placeholder.png';
import LoadingModal from './LoadingModal.js';
import Subscription from './Subscription.js';
import ConfirmModal from './ConfirmModal.js';
import ScheduleDetailsModal from './ScheduleDetailsModal.js';
import EditBasicDataModal from './EditBasicDataModal.js';
import Settings from './Settings.js';
import Schedules from './Schedules.js';
import Notifications from './Notifications.js';
import Consultations from './Consultations.js';
import MedicalRecord from './MedicalRecord.js';
import Pacientes from './Patients.js';
import './Dashboard.css';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.locale('pt-br');

function InProgressWidget({ schedule, onClose, onNavigate }) {
  const [elapsed, setElapsed] = React.useState('');

  React.useEffect(() => {
    const calc = () => {
      const diff = Math.floor((Date.now() - new Date(schedule.createdAt).getTime()) / 1000);
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      if (h > 0) setElapsed(`${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`);
      else setElapsed(`${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`);
    };
    calc();
    const t = setInterval(calc, 1000);
    return () => clearInterval(t);
  }, [schedule.createdAt]);

  return (
    <div style={{
      position: 'fixed', bottom: '24px', right: '24px', zIndex: 999,
      background: '#fff', borderRadius: '18px',
      boxShadow: '0 8px 32px rgba(113,101,255,0.18), 0 2px 8px rgba(0,0,0,0.08)',
      border: '1.5px solid rgba(113,101,255,0.2)',
      padding: '16px 20px', minWidth: '300px', maxWidth: '340px',
      animation: 'mrFadeIn 0.3s ease',
    }}>
      {/* Close */}
      <button onClick={onClose} style={{ position: 'absolute', top: '12px', right: '12px', border: 'none', background: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '18px', lineHeight: 1, padding: '2px' }}>×</button>

      {/* Header: photo + name + badge */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <img
            src={schedule.pet?.photo || ''}
            alt={schedule.pet?.name}
            style={{ width: 52, height: 52, borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(113,101,255,0.3)' }}
            onError={e => { e.target.style.display = 'none'; }}
          />
          <div style={{ position: 'absolute', bottom: 2, right: 2, width: 12, height: 12, borderRadius: '50%', background: '#f59e0b', border: '2px solid #fff' }} />
        </div>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '16px', fontWeight: '800', color: '#111' }}>{schedule.pet?.name}</span>
            <span style={{ fontSize: '11px', fontWeight: '700', background: '#FEF3C7', color: '#D97706', padding: '2px 8px', borderRadius: '20px', whiteSpace: 'nowrap' }}>Em atendimento</span>
          </div>
          {schedule.owner?.name && (
            <span style={{ fontSize: '12px', color: '#9ca3af' }}>{schedule.owner.name}</span>
          )}
        </div>
      </div>

      {/* Info rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#374151' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7165FF" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          <span>Tempo: <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{elapsed}</strong></span>
        </div>
        {schedule.chiefComplaint ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#374151' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7165FF" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/></svg>
            <span>Queixa: <strong>{schedule.chiefComplaint}</strong></span>
          </div>
        ) : schedule.service?.name ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#374151' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7165FF" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/></svg>
            <span>Serviço: <strong>{schedule.service.name}</strong></span>
          </div>
        ) : null}
      </div>

      {/* Action */}
      <button
        onClick={() => onNavigate(schedule)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '10px', border: 'none', borderRadius: '12px', background: 'var(--color-main)', color: '#fff', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}
      >
        Prontuário
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
      </button>
    </div>
  );
}

function QuickStartModal({ isOpen, onClose, entityId, onSelect }) {
  const [search, setSearch] = React.useState('');
  const [results, setResults] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    if (isOpen) { setSearch(''); setResults([]); setTimeout(() => inputRef.current?.focus(), 100); }
  }, [isOpen]);

  React.useEffect(() => {
    if (!search || search.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      const r = await medicalRecordService.getPets({ entityId, search, limit: 8 });
      setResults(r.success ? (r.pets || []) : []);
      setLoading(false);
    }, 300);
    return () => clearTimeout(t);
  }, [search, entityId]);

  if (!isOpen) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '480px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid #f0f0f0' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#111' }}>Iniciar Atendimento</h2>
            <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#9ca3af' }}>Selecione o paciente para iniciar</p>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: '#f3f4f6', cursor: 'pointer', width: '32px', height: '32px', borderRadius: '50%', fontSize: '18px', color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>
        {/* Search */}
        <div style={{ padding: '16px 24px', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#f9fafb', border: '1.5px solid #e5e7eb', borderRadius: '10px', padding: '10px 14px' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            <input
              ref={inputRef}
              type="text"
              placeholder="Buscar paciente por nome ou raça..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ border: 'none', outline: 'none', fontSize: '14px', color: '#374151', background: 'transparent', width: '100%' }}
            />
            {search && <button onClick={() => setSearch('')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '18px', lineHeight: 1, padding: 0 }}>×</button>}
          </div>
        </div>
        {/* Results */}
        <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: '32px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>Buscando...</div>
          ) : search.length < 2 ? (
            <div style={{ padding: '32px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>Digite pelo menos 2 caracteres para buscar</div>
          ) : results.length === 0 ? (
            <div style={{ padding: '32px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>Nenhum paciente encontrado para "{search}"</div>
          ) : results.map(pet => (
            <div key={pet.id}
              onClick={() => onSelect(pet)}
              style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 24px', cursor: 'pointer', borderBottom: '1px solid #f9fafb', transition: 'background 0.1s' }}
              onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
            >
              <div style={{ width: 40, height: 40, borderRadius: '50%', overflow: 'hidden', background: '#f3f4f6', flexShrink: 0 }}>
                <img src={pet.photo || '/placeholder.png'} alt={pet.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={e => { e.target.style.display = 'none'; }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#111' }}>{pet.name}</p>
                <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#9ca3af' }}>
                  {[SPECIES_LABEL[pet.species] || pet.species, pet.breed, pet.owner?.name || pet.ownerName].filter(Boolean).join(' · ')}
                </p>
              </div>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [entity, setEntity] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [upcomingV3, setUpcomingV3] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [currentView, setCurrentView] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [error, setError] = useState('');
  const [showSubscription, setShowSubscription] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showDeletePhotoModal, setShowDeletePhotoModal] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedScheduleId, setSelectedScheduleId] = useState(null);
  const [showRequirementsModal, setShowRequirementsModal] = useState(false);
  const [showEditBasicDataModal, setShowEditBasicDataModal] = useState(false);
  const [settingsTab, setSettingsTab] = useState('insurances');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [pendingConsultationSchedule, setPendingConsultationSchedule] = useState(null);
  const [pendingViewSchedule, setPendingViewSchedule] = useState(null);
  const [clinicIndicators, setClinicIndicators] = useState(null);
  const [quickStartOpen, setQuickStartOpen] = useState(false);
  const [inProgressDismissed, setInProgressDismissed] = useState(false);
  const fileInputRef = useRef(null);

  // Wrapper para logout com analytics
  const handleLogout = () => {
    analyticsEvents.logout();
    onLogout();
  };

  // Prevenir scroll do body quando sidebar estiver aberto em mobile
  useEffect(() => {
    if (isSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup ao desmontar
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isSidebarOpen]);

  // Título dinâmico baseado na view atual
  const getPageTitle = () => {
    switch (currentView) {
      case 'reviews':
        return 'Avaliações';
      case 'patients':
        return 'Pacientes';
      case 'schedules':
        return 'Agenda';
      case 'settings':
        return 'Configurações';
      case 'notifications':
        return 'Notificações';
      case 'medical-record':
        return 'Tutores';
      default:
        return 'Dashboard';
    }
  };

  usePageTitle(getPageTitle());

  useEffect(() => {
    loadEntityData();
  }, []);

  useEffect(() => {
    // Recarregar dados da entidade quando o usuário for atualizado
    if (user && !loading) {
      const reloadCurrentView = async () => {
        try {
          const result = await entityService.getMyEntities();
          if (result.success && result?.entities?.length > 0) {
            const entityData = result.entities[0];
            setEntity(entityData);

            // Recarregar dados específicos da view atual
            if (currentView === 'schedules' || currentView === 'dashboard') {
              const schedulesResult = await scheduleService.getEntitySchedules(entityData.id);
              if (schedulesResult.success) {
                setSchedules(schedulesResult.schedules);
              }
            }

            if (currentView === 'reviews' || currentView === 'dashboard') {
              const reviewsResult = await reviewService.getEntityReviews(entityData.id);
              if (reviewsResult.success) {
                setReviews(reviewsResult.reviews);
              }
            }
          }
        } catch (err) {
          console.error('Erro ao recarregar dados:', err);
        }
      };
      reloadCurrentView();
    }
  }, [user]);

  useEffect(() => {
    // Sincronizar view com a URL
    const path = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');

    if (path === '/reviews') {
      setCurrentView('reviews');
    } else if (path === '/settings') {
      setCurrentView('settings');
      if (tab) {
        setSettingsTab(tab);
      }
    } else if (path === '/patients') {
      setCurrentView('patients');
    } else if (path === '/schedules') {
      setCurrentView('schedules');
    } else if (path === '/appointments') {
      setCurrentView('appointments');
    } else if (path === '/notifications') {
      setCurrentView('notifications');
    } else if (path === '/medical-record' || path.startsWith('/medical-record/')) {
      setCurrentView('medical-record');
    } else {
      setCurrentView('dashboard');
    }

    // Fechar sidebar em mobile quando a rota mudar
    setIsSidebarOpen(false);
  }, [location.pathname, location.search]);

  // As imagens vêm de entity.images (5 posições fixas)

  const loadEntityData = async () => {
    try {
      setLoading(true);

      // Atualizar dados do usuário
      await refreshUser();

      const result = await entityService.getMyEntities();
      if (result.success && result?.entities?.length > 0) {
        const entityData = result.entities[0];
        setEntity(entityData);

        // Buscar agendamentos da entidade (lista completa)
        const schedulesResult = await scheduleService.getEntitySchedules(entityData.id);
        if (schedulesResult.success) {
          setSchedules(schedulesResult.schedules);
        }

        // Buscar reviews da entidade
        const reviewsResult = await reviewService.getEntityReviews(entityData.id);
        if (reviewsResult.success) {
          setReviews(reviewsResult.reviews);
        }

        // Buscar notificações do usuário
        const notificationsResult = await notificationService.getNotifications();
        if (notificationsResult.success) {
          setNotifications(notificationsResult.notifications);
        }

        // Buscar indicadores da clínica v3
        const today = dayjs().format('YYYY-MM-DD');
        const indicatorsResult = await clinicService.getIndicators(entityData.id, today);
        if (indicatorsResult.success && indicatorsResult.indicators) {
          setClinicIndicators(indicatorsResult.indicators);
        }

        // Próximos agendamentos v3 (hoje)
        const agendaResult = await agendaService.getAgenda({
          entityId: entityData.id,
          view: 'day',
          date: today,
        });
        if (agendaResult.success) {
          const upcoming = (agendaResult.schedules || [])
            .filter(s => !['canceled', 'no_show', 'blocked'].includes(s.status))
            .sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime))
            .slice(0, 5);
          setUpcomingV3(upcoming);
        }
      }
    } catch (err) {
      setError('Ocorreu um erro ao carregar os dados. Por favor, tente novamente mais tarde.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleClick = (scheduleId, status) => {
    analyticsEvents.viewSchedules();
    if (status === 'completed') {
      navigate(`/appointment/${scheduleId}`);
      return;
    }
    setSelectedScheduleId(scheduleId);
    setShowScheduleModal(true);
  };

  const handleScheduleModalClose = () => {
    setShowScheduleModal(false);
    setSelectedScheduleId(null);
  };

  const handleScheduleStatusChange = (scheduleId, newStatus) => {
    // Rastrear evento baseado no novo status
    if (newStatus === 'CONFIRMED') {
      analyticsEvents.confirmSchedule();
    } else if (newStatus === 'CANCELED') {
      analyticsEvents.cancelSchedule();
    }

    // Atualizar o status do agendamento na lista
    setSchedules(prevSchedules =>
      prevSchedules.map(schedule =>
        schedule.id === scheduleId
          ? { ...schedule, status: newStatus }
          : schedule
      )
    );
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    const currentImages = entity?.images || [];

    // Pegar a posição clicada do dataset
    const clickedPositionStr = event.target.dataset.clickedPosition;
    let position = clickedPositionStr !== 'null' && clickedPositionStr !== null
      ? parseInt(clickedPositionStr)
      : null;

    // Se não foi passada uma posição específica, encontrar a primeira vazia
    if (position === null) {
      position = currentImages.findIndex(img => !img.url);
      if (position === -1) {
        toast.error('Todas as 5 posições estão ocupadas.');
        return;
      }
    }

    setUploadingPhoto(true);
    setLoadingMessage('Enviando foto...');

    try {
      const file = files[0]; // Apenas primeira foto
      const result = await photoService.uploadPhoto(file, entity.id, position, currentImages);

      if (result.success) {
        analyticsEvents.updatePhoto();
        // Atualizar entity com as novas imagens
        setEntity(prev => ({
          ...prev,
          images: result.images
        }));
        // Atualizar dados do usuário
        await refreshUser();
      } else {
        toast.error(result.error || 'Erro ao enviar foto');
      }
    } catch (err) {
      console.error('Erro ao fazer upload:', err);
      toast.error('Erro ao enviar foto.');
    } finally {
      setUploadingPhoto(false);
      setLoadingMessage('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeletePhoto = async (position) => {
    setPhotoToDelete(position);
    setShowDeletePhotoModal(true);
  };

  const confirmDeletePhoto = async () => {

    setLoadingMessage('Excluindo foto...');

    try {
      const currentImages = entity?.images || [];
      const result = await photoService.deletePhoto(entity.id, photoToDelete, currentImages);

      if (result.success) {
        // Atualizar entity com as novas imagens
        setEntity(prev => ({
          ...prev,
          images: result.images
        }));
        toast.success('Foto excluída com sucesso!');
        // Atualizar dados do usuário
        await refreshUser();
      } else {
        toast.error(result.error || 'Erro ao excluir foto');
      }
    } catch (err) {
      console.error('Erro ao deletar foto:', err);
      toast.error('Erro ao excluir foto.');
    } finally {
      setLoadingMessage('');
      setPhotoToDelete(null);
    }
  };

  const handleAddPhotoClick = (position = null) => {
    const currentImages = entity?.images || [];

    // Se clicou em uma posição específica, sempre permitir (vai substituir)
    if (position !== null) {
      // Armazenar a posição clicada para usar no handleFileSelect
      fileInputRef.current.dataset.clickedPosition = position;
      fileInputRef.current?.click();
      return;
    }

    // Se não clicou em posição específica (botão header), verificar se há slot vazio
    const hasEmptySlot = currentImages.some(img => !img.url);
    if (!hasEmptySlot) {
      toast.error('Todas as 5 posições estão ocupadas. Delete ou substitua uma foto.');
      return;
    }

    fileInputRef.current.dataset.clickedPosition = null;
    fileInputRef.current?.click();
  };

  const handleToggleStatus = async () => {
    if (!entity) return;

    const newStatus = !entity.active;

    // Se está tentando ativar, verificar requisitos obrigatórios
    if (newStatus) {
      const hasBasicData = entity?.phone && entity?.address && entity?.addressCity;
      const hasServices = entity?.services?.some(service => service.active) || false;
      const hasPlan = entity?.plan?.active || false;

      // Se todos os requisitos estão ok, ativar
      if (hasBasicData && hasServices && hasPlan) {
        try {
          setLoadingMessage('Ativando negócio...');

          const result = await entityService.toggleEntityStatus(entity.id, newStatus);

          if (result.success) {
            setEntity({ ...entity, active: newStatus });
            toast.success('Negócio ativado!');
            // Atualizar dados do usuário
            await refreshUser();
          } else {
            toast.error('Erro ao atualizar status');
          }
        } catch (err) {
          console.error(err);
          toast.error('Erro ao atualizar status');
        } finally {
          setLoadingMessage('');
        }
      } else {
        // Se algum requisito não está ok, exibir modal com as etapas
        setShowRequirementsModal(true);
      }
    } else {
      // Se está desativando, apenas desativar sem verificações
      try {
        setLoadingMessage('Desativando negócio...');

        const result = await entityService.toggleEntityStatus(entity.id, newStatus);

        if (result.success) {
          setEntity({ ...entity, active: newStatus });
          toast.success('Negócio desativado!');
          // Atualizar dados do usuário
          await refreshUser();
        } else {
          toast.error('Erro ao atualizar status');
        }
      } catch (err) {
        console.error(err);
        toast.error('Erro ao atualizar status');
      } finally {
        setLoadingMessage('');
      }
    }
  };

  const handleSubscribe = async (plan) => {
    // Aqui você pode implementar a lógica de checkout
    // Por exemplo, criar uma sessão de checkout do Stripe
    console.log('Plano selecionado:', plan);

    // Exemplo de integração com backend
    try {
      setLoadingMessage('Processando assinatura...');

      // Simulação de chamada à API
      // const response = await fetch('/create-checkout-session', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ lookup_key: plan.lookupKey, entity_id: entity.id })
      // });
      // const session = await response.json();
      // window.location.href = session.url;

      // Por enquanto, apenas fecha o modal
      toast.success(`Assinatura do plano ${plan.title} iniciada! (Integração com pagamento pendente)`);
      setShowSubscription(false);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao processar assinatura');
    } finally {
      setLoadingMessage('');
    }
  };

  const handleSaveBasicData = async (formData) => {
    try {
      setLoadingMessage('Salvando dados...');

      console.log('Enviando dados para atualização:', formData);
      const result = await entityService.updateBasicData(entity.id, formData);
      console.log('Resultado da atualização:', result);

      if (result.success) {
        setEntity(prev => ({
          ...prev,
          ...formData
        }));
        toast.success('Dados atualizados com sucesso!');
        setShowEditBasicDataModal(false);
        // Atualizar dados do usuário
        await refreshUser();
      } else {
        toast.error('Erro ao atualizar dados');
      }
    } catch (err) {
      console.error('Erro ao salvar dados básicos:', err);
      toast.error('Erro ao atualizar dados');
    } finally {
      setLoadingMessage('');
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        {/* Header Shimmer */}
        <header className="dashboard-header">
          <div className="header-left">
            <div className="shimmer shimmer-icon" style={{ width: '40px', height: '40px', borderRadius: '8px' }}></div>
            <div className="shimmer shimmer-logo"></div>
            <div className="shimmer shimmer-text" style={{ width: '200px' }}></div>
          </div>
          <div className="header-right">
            <div className="shimmer shimmer-button"></div>
            <div className="shimmer shimmer-icon"></div>
          </div>
        </header>

        <main className="dashboard-main">
          {/* Sidebar Shimmer */}
          <aside className="dashboard-sidebar">
            <nav className="sidebar-nav">
              {[1, 2, 3, 4, 5, 6, 7].map(i => (
                <div key={i} className="shimmer shimmer-nav-item"></div>
              ))}
            </nav>
            <div className="sidebar-profile">
              <div className="shimmer shimmer-avatar"></div>
              <div style={{ flex: 1 }}>
                <div className="shimmer shimmer-text" style={{ width: '100px', marginBottom: '8px' }}></div>
                <div className="shimmer shimmer-text" style={{ width: '80px' }}></div>
              </div>
            </div>
          </aside>

          {/* Content Shimmer */}
          <div className="dashboard-content">
            {/* Stats Cards */}
            <div className="stats-grid">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="shimmer shimmer-card"></div>
              ))}
            </div>

            {/* Charts Grid */}
            <div className="charts-grid">
              <div className="shimmer shimmer-chart-large"></div>
              <div className="shimmer shimmer-chart-small"></div>
            </div>

            {/* Schedule List */}
            <div className="schedule-section">
              <div className="shimmer shimmer-title" style={{ width: '250px', marginBottom: '20px' }}></div>
              {[1, 2, 3].map(i => (
                <div key={i} className="shimmer shimmer-schedule-item"></div>
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error || !entity) {
    return (
      <div className="dashboard-container">
        {/* Empty State Header */}
        <header className="empty-state-header">
          <div className="header-left">
            <Logo className="dashboard-logo" />
            <p className="panel-subtitle">Painel do Veterinário</p>
          </div>
          <div className="header-right">
            <Link to="/profile" className="empty-state-profile-btn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              Perfil
            </Link>
            <button onClick={handleLogout} className="empty-state-logout-btn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              Sair
            </button>
          </div>
        </header>

        {/* Empty State Content */}
        <div className="empty-state-content">
          <div className="empty-state-card">
            <div className="empty-state-icon">
              <LogoDark className="empty-state-logo" width="120" height="120" />
            </div>
            {error ? (
              <>
                <h1 className="empty-state-title">Erro ao carregar dados</h1>
                <p className="empty-state-description">{error}</p>
                <button className="empty-state-create-btn" onClick={() => loadEntityData()}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 4 23 10 17 10" />
                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                  </svg>
                  Tentar Novamente
                </button>
              </>
            ) : (
              <>
                <h1 className="empty-state-title">Bem-vindo ao Vetsse!</h1>
                <p className="empty-state-description">
                  Para começar a usar o painel, você precisa criar seu negócio.
                  Um negócio representa seu consultório, clínica ou estabelecimento veterinário.
                </p>
                <button className="empty-state-create-btn" onClick={() => navigate('/create-entity')}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Criar Meu Negócio
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  const profileCompletion = 75; // Calculado baseado nos dados

  // Calcular requisitos obrigatórios
  const hasBasicData = entity?.phone && entity?.address && entity?.addressCity;
  const hasServices = entity?.services?.some(service => service.active) || false;
  const hasPlan = entity?.plan?.active || false;

  const completedRequirements = [hasBasicData, hasServices, hasPlan].filter(Boolean).length;
  const totalRequirements = 3;

  // Calcular estatísticas baseadas nos dados da entidade
  const servicesCount = entity?.services?.length || 0;

  // Verificar se há serviços ativos
  const hasActiveServices = entity?.services?.some(service => service.active) || false;
  const showServiceAlert = !hasActiveServices;
  const insurancesCount = entity?.insurances?.length || 0;
  const reviewsCount = entity?.reviews?.length || 0;

  // Calcular estatísticas de agendamentos
  const totalSchedules = schedules.length;
  const completedSchedules = schedules.filter(s => s.status === 'completed').length;

  // Próximos agendamentos (confirmados, ordenados por data) - limitado a 3
  const now = new Date();
  const upcomingSchedules = schedules
    .filter(s => {
      const scheduleDate = new Date(s.dateTime);
      return (s.status === 'confirmed' || s.status === 'pending') &&
        scheduleDate > now;
    })
    .sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime))
    .slice(0, 3);

  // Função para formatar data
  const formatDate = (dateString) => {
    return dayjs.utc(dateString).format('DD MMM');
  };

  // Função para formatar hora
  const formatTime = (dateString) => {
    return dayjs.utc(dateString).format('HH:mm');
  };

  // Função para obter status label
  const getStatusLabel = (status) => {
    const labels = {
      'confirmed': 'Confirmado',
      'pending': 'Pendente',
      'completed': 'Concluído',
      'rejected': 'Rejeitado'
    };
    return labels[status] || status;
  };

  // Função para obter classe de status
  const getStatusClass = (status) => {
    const classes = {
      'confirmed': 'status-confirmed',
      'pending': 'status-pending',
      'completed': 'status-completed',
      'rejected': 'status-rejected'
    };
    return classes[status] || '';
  };

  return (
    <div className="dashboard-container">
      <QuickStartModal
        isOpen={quickStartOpen}
        onClose={() => setQuickStartOpen(false)}
        entityId={entity?.id}
        onSelect={(pet) => {
          setQuickStartOpen(false);
          navigate(`/medical-record/pet/${pet.id}/consultation/new`, { state: { pet: { ...pet, owner: pet.owner } } });
          setCurrentView('medical-record');
        }}
      />
      <ConfirmModal
        isOpen={showDeletePhotoModal}
        onClose={() => {
          setShowDeletePhotoModal(false);
          setPhotoToDelete(null);
        }}
        onConfirm={confirmDeletePhoto}
        title="Excluir Foto"
        message="Deseja realmente excluir esta foto? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        type="danger"
      />

      <LoadingModal
        isOpen={!!loadingMessage}
        message={loadingMessage}
      />

      {/* Header */}
      <header className="dashboard-header">
        <div className="header-left">
          <button
            className="hamburger-btn"
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            aria-label="Menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          <Logo className="dashboard-logo" />
          <p className="panel-subtitle">{entity?.name || 'Painel do Veterinário'}</p>
        </div>
        <div className="header-right">
          {completedRequirements < totalRequirements && (
            <button
              className="requirements-badge-btn"
              onClick={() => setShowRequirementsModal(true)}
              title="Ver requisitos obrigatórios"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 11l3 3L22 4" />
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
              </svg>
              <span>{completedRequirements}/{totalRequirements}</span>
            </button>
          )}
          <button
            className={`toggle-status-btn ${entity?.active ? 'active' : 'inactive'}`}
            onClick={handleToggleStatus}
            data-tooltip={entity?.active ? 'Ocultar meu negócio no aplicativo' : 'Exibir meu negócio no aplicativo'}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {entity?.active ? (
                <>
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </>
              ) : (
                <>
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </>
              )}
            </svg>
            {entity?.active ? 'Visível' : 'Oculto'}
          </button>
          <button className="notification-btn" aria-label="Notificações" onClick={() => navigate('/notifications')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            {notifications.filter(n => !n.isOpened).length > 0 && (
              <span className="notification-badge">
                {notifications.filter(n => !n.isOpened).length}
              </span>
            )}
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="dashboard-main">
        {/* Overlay para mobile */}
        {isSidebarOpen && (
          <div
            className="sidebar-overlay"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside className={`dashboard-sidebar ${isSidebarOpen ? 'open' : ''}`}>
          <nav className="sidebar-nav">
            <a
              href="#"
              className={`nav-item ${currentView === 'dashboard' ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                navigate('/dashboard');
                setIsSidebarOpen(false);
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
              Início
            </a>
            <a
              href="#"
              className={`nav-item ${currentView === 'schedules' ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                navigate('/schedules');
                setIsSidebarOpen(false);
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              Agenda
            </a>
            <a
              href="#"
              className={`nav-item ${currentView === 'patients' ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                navigate('/patients');
                setIsSidebarOpen(false);
              }}
            >
              <svg width="18" height="18" viewBox="0 0 512 512" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M382.825,304.576a131.562,131.562,0,0,0-253.65,0l-18.248,66.15A80,80,0,0,0,188.046,472H323.954a80,80,0,0,0,77.119-101.274Zm-20.682,116.5A47.638,47.638,0,0,1,323.954,440H188.046a48,48,0,0,1-46.272-60.765l18.248-66.149a99.563,99.563,0,0,1,191.956,0l18.248,66.149A47.636,47.636,0,0,1,362.143,421.08Z"/>
                <path d="M146.1,230.31c2.784-17.4-.908-36.027-10.4-52.463S111.92,148.9,95.463,142.611c-17.624-6.731-35.6-5.659-50.634,3.017C14.942,162.884,7.077,205.413,27.3,240.433c9.489,16.436,23.778,28.95,40.235,35.236a64.058,64.058,0,0,0,22.863,4.371,55.133,55.133,0,0,0,27.771-7.389C133.194,263.974,143.114,248.937,146.1,230.31Zm-31.6-5.058c-1.43,8.929-5.81,15.92-12.333,19.686S87.4,249,78.95,245.775c-9.613-3.671-18.115-11.251-23.941-21.342-11.2-19.4-8.538-42.8,5.82-51.092a23.483,23.483,0,0,1,11.847-3.058A31.951,31.951,0,0,1,84.044,172.5c9.613,3.673,18.115,11.252,23.941,21.343S116.124,215.091,114.5,225.252Z"/>
                <path d="M149.566,164.017c11.362,9.083,24.337,13.813,37.458,13.812a54.965,54.965,0,0,0,11.689-1.261c33.723-7.331,54.17-45.443,45.58-84.958h0c-4.03-18.546-13.828-34.817-27.588-45.818-14.735-11.78-32.189-16.239-49.147-12.551-33.722,7.33-54.169,45.442-45.58,84.957C126.009,136.745,135.807,153.016,149.566,164.017Zm24.788-99.506a22.258,22.258,0,0,1,4.732-.5c5.948,0,12.066,2.327,17.637,6.781,8.037,6.425,13.826,16.234,16.3,27.621h0c4.76,21.895-4.906,43.368-21.107,46.89-7.361,1.6-15.305-.628-22.367-6.275-8.037-6.426-13.826-16.235-16.3-27.621C148.488,89.506,158.154,68.033,174.354,64.511Z"/>
                <path d="M467.171,145.628c-15.028-8.676-33.013-9.748-50.634-3.017-16.457,6.287-30.746,18.8-40.235,35.236s-13.182,35.067-10.4,52.463c2.982,18.627,12.9,33.664,27.931,42.341a55.123,55.123,0,0,0,27.771,7.389,64.054,64.054,0,0,0,22.863-4.371c16.457-6.286,30.746-18.8,40.235-35.236C504.923,205.413,497.058,162.884,467.171,145.628Zm-10.18,78.805c-5.826,10.091-14.328,17.671-23.941,21.342-8.446,3.228-16.692,2.931-23.215-.837s-10.9-10.757-12.333-19.686c-1.626-10.161.686-21.314,6.513-31.4s14.328-17.67,23.941-21.343a31.955,31.955,0,0,1,11.368-2.221,23.483,23.483,0,0,1,11.847,3.058C465.529,181.631,468.194,205.028,456.991,224.433Z"/>
                <path d="M313.287,176.568a54.965,54.965,0,0,0,11.689,1.261c13.12,0,26.1-4.729,37.458-13.812,13.759-11,23.557-27.272,27.588-45.818,8.589-39.515-11.858-77.627-45.58-84.957-16.957-3.686-34.412.77-49.147,12.551-13.76,11-23.558,27.272-27.588,45.817C259.117,131.125,279.564,169.237,313.287,176.568Zm-14.31-78.16h0c2.474-11.387,8.263-21.2,16.3-27.621,5.572-4.454,11.689-6.781,17.637-6.781a22.258,22.258,0,0,1,4.732.5c16.2,3.522,25.866,25,21.107,46.89-2.476,11.387-8.265,21.2-16.3,27.622-7.061,5.646-15,7.874-22.367,6.275C303.883,141.776,294.217,120.3,298.977,98.408Z"/>
              </svg>
              Pacientes
            </a>
            <a
              href="#"
              className={`nav-item ${currentView === 'medical-record' ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                navigate('/medical-record');
                setIsSidebarOpen(false);
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="8" r="5" />
                <path d="M12 14c-5 0-8 2.5-8 4v2h16v-2c0-1.5-3-4-8-4z" />
              </svg>
              Tutores
            </a>
            <a
              href="#"
              className={`nav-item ${currentView === 'reviews' ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                navigate('/reviews');
                setIsSidebarOpen(false);
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
              Avaliações
            </a>
            <a
              href="#"
              className={`nav-item ${currentView === 'notifications' ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                navigate('/notifications');
                setIsSidebarOpen(false);
              }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                Notificações
              </div>
              {notifications.filter(n => !n.isOpened).length > 0 && (
                <span style={{
                  fontSize: '11px',
                  fontWeight: '700',
                  color: currentView === 'notifications' ? '#171717' : '#7165FF',
                  background: currentView === 'notifications' ? '#FFFFFF' : 'rgba(113, 101, 255, 0.1)',
                  padding: '4px 8px',
                  borderRadius: '12px',
                  minWidth: '24px',
                  textAlign: 'center',
                  transition: 'all 0.3s'
                }}>
                  {notifications.filter(n => !n.isOpened).length}
                </span>
              )}
            </a>
            <Link
              to="/profile"
              className="nav-item"
              onClick={() => setIsSidebarOpen(false)}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              Meu Perfil
            </Link>
            <a
              href="#"
              className={`nav-item ${currentView === 'settings' ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                navigate('/settings');
                setIsSidebarOpen(false);
              }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
                Configurações
              </div>
              {showServiceAlert && (
                <span className={`service-alert-badge ${currentView === 'settings' ? 'active' : ''}`}>
                  1
                </span>
              )}
            </a>
          </nav>

          {/* User Profile */}
          <div className="sidebar-profile">
            {user.image && (
              <img
                src={user.image}
                alt={user.name}
                className="profile-avatar-dashboard"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = placeholderImage;
                }}
              />
            )}
            <div className="profile-info">
              <p className="profile-name">{user.name}</p>
              <p className="profile-role">{user.email}</p>
            </div>
            <button onClick={handleLogout} className="logout-btn" title="Sair">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </button>
          </div>
        </aside>

        {/* Content */}
        <div className="dashboard-content">
          {currentView === 'dashboard' ? (
            <>
              {/* Welcome Section */}
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '32px', gap: '16px', flexWrap: 'wrap' }}>
                <div>
                  <h1 style={{ margin: 0, fontSize: '36px', fontWeight: '800', color: '#111', lineHeight: 1.15 }}>
                    Olá, {user.name?.split(' ').slice(0, 3).join(' ')}!
                  </h1>
                  <p style={{ margin: '8px 0 0', fontSize: '15px', color: '#6b7280' }}>
                    Você tem{' '}
                    <strong style={{ color: 'var(--color-main)', fontWeight: '700' }}>
                      {clinicIndicators?.today?.total ?? 0} {(clinicIndicators?.today?.total ?? 0) === 1 ? 'atendimento' : 'atendimentos'}
                    </strong>{' '}
                    {(clinicIndicators?.today?.total ?? 0) === 1 ? 'programado' : 'programados'} para hoje.
                  </p>
                </div>
                <button
                  onClick={() => setQuickStartOpen(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 28px', border: 'none', borderRadius: '14px', background: 'var(--color-main)', color: '#fff', fontSize: '15px', fontWeight: '700', cursor: 'pointer', flexShrink: 0, boxShadow: '0 4px 16px rgba(113,101,255,0.35)' }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
                  Iniciar Atendimento
                </button>
              </div>

              {/* Stats Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '28px' }}>
                {/* Atendimentos Hoje */}
                <a href="#" onClick={(e) => { e.preventDefault(); navigate('/schedules'); }} style={{ textDecoration: 'none' }}>
                  <div style={{ background: '#fff', borderRadius: '16px', padding: '20px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0', cursor: 'pointer', transition: 'box-shadow 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(113,101,255,0.12)'}
                    onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 6px rgba(0,0,0,0.06)'}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                      <div style={{ width: 42, height: 42, borderRadius: '12px', background: 'rgba(113,101,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-main)' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                      </div>
                      {clinicIndicators?.today?.completed > 0 && (
                        <span style={{ fontSize: '11px', fontWeight: '700', color: '#10b981', background: 'rgba(16,185,129,0.1)', padding: '3px 8px', borderRadius: '20px' }}>
                          {clinicIndicators.today.completed} {clinicIndicators.today.completed === 1 ? 'finalizado' : 'finalizados'}
                        </span>
                      )}
                    </div>
                    <p style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Atendimentos Hoje</p>
                    <p style={{ margin: 0, fontSize: '32px', fontWeight: '800', color: '#111', lineHeight: 1 }}>
                      {clinicIndicators?.today?.total ?? 0}
                    </p>
                  </div>
                </a>

                {/* Tutores */}
                <a href="#" onClick={(e) => { e.preventDefault(); navigate('/medical-record'); }} style={{ textDecoration: 'none' }}>
                  <div style={{ background: '#fff', borderRadius: '16px', padding: '20px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0', cursor: 'pointer', transition: 'box-shadow 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(16,185,129,0.12)'}
                    onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 6px rgba(0,0,0,0.06)'}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                      <div style={{ width: 42, height: 42, borderRadius: '12px', background: 'rgba(16,185,129,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10b981' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                      </div>
                    </div>
                    <p style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Tutores</p>
                    <p style={{ margin: 0, fontSize: '32px', fontWeight: '800', color: '#111', lineHeight: 1 }}>
                      {(clinicIndicators?.overview?.totalOwners ?? 0).toLocaleString('pt-BR')}
                    </p>
                  </div>
                </a>

                {/* Pacientes */}
                <a href="#" onClick={(e) => { e.preventDefault(); navigate('/patients'); }} style={{ textDecoration: 'none' }}>
                  <div style={{ background: '#fff', borderRadius: '16px', padding: '20px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0', cursor: 'pointer', transition: 'box-shadow 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(245,158,11,0.12)'}
                    onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 6px rgba(0,0,0,0.06)'}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                      <div style={{ width: 42, height: 42, borderRadius: '12px', background: 'rgba(245,158,11,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f59e0b' }}>
                        <svg width="18" height="18" viewBox="0 0 512 512" fill="currentColor"><path d="M382.825,304.576a131.562,131.562,0,0,0-253.65,0l-18.248,66.15A80,80,0,0,0,188.046,472H323.954a80,80,0,0,0,77.119-101.274Zm-20.682,116.5A47.638,47.638,0,0,1,323.954,440H188.046a48,48,0,0,1-46.272-60.765l18.248-66.149a99.563,99.563,0,0,1,191.956,0l18.248,66.149A47.636,47.636,0,0,1,362.143,421.08Z"/><path d="M146.1,230.31c2.784-17.4-.908-36.027-10.4-52.463S111.92,148.9,95.463,142.611c-17.624-6.731-35.6-5.659-50.634,3.017C14.942,162.884,7.077,205.413,27.3,240.433c9.489,16.436,23.778,28.95,40.235,35.236a64.058,64.058,0,0,0,22.863,4.371,55.133,55.133,0,0,0,27.771-7.389C133.194,263.974,143.114,248.937,146.1,230.31Zm-31.6-5.058c-1.43,8.929-5.81,15.92-12.333,19.686S87.4,249,78.95,245.775c-9.613-3.671-18.115-11.251-23.941-21.342-11.2-19.4-8.538-42.8,5.82-51.092a23.483,23.483,0,0,1,11.847-3.058A31.951,31.951,0,0,1,84.044,172.5c9.613,3.673,18.115,11.252,23.941,21.343S116.124,215.091,114.5,225.252Z"/><path d="M149.566,164.017c11.362,9.083,24.337,13.813,37.458,13.812a54.965,54.965,0,0,0,11.689-1.261c33.723-7.331,54.17-45.443,45.58-84.958h0c-4.03-18.546-13.828-34.817-27.588-45.818-14.735-11.78-32.189-16.239-49.147-12.551-33.722,7.33-54.169,45.442-45.58,84.957C126.009,136.745,135.807,153.016,149.566,164.017Zm24.788-99.506a22.258,22.258,0,0,1,4.732-.5c5.948,0,12.066,2.327,17.637,6.781,8.037,6.425,13.826,16.234,16.3,27.621h0c4.76,21.895-4.906,43.368-21.107,46.89-7.361,1.6-15.305-.628-22.367-6.275-8.037-6.426-13.826-16.235-16.3-27.621C148.488,89.506,158.154,68.033,174.354,64.511Z"/><path d="M467.171,145.628c-15.028-8.676-33.013-9.748-50.634-3.017-16.457,6.287-30.746,18.8-40.235,35.236s-13.182,35.067-10.4,52.463c2.982,18.627,12.9,33.664,27.931,42.341a55.123,55.123,0,0,0,27.771,7.389,64.054,64.054,0,0,0,22.863-4.371c16.457-6.286,30.746-18.8,40.235-35.236C504.923,205.413,497.058,162.884,467.171,145.628Zm-10.18,78.805c-5.826,10.091-14.328,17.671-23.941,21.342-8.446,3.228-16.692,2.931-23.215-.837s-10.9-10.757-12.333-19.686c-1.626-10.161.686-21.314,6.513-31.4s14.328-17.67,23.941-21.343a31.955,31.955,0,0,1,11.368-2.221,23.483,23.483,0,0,1,11.847,3.058C465.529,181.631,468.194,205.028,456.991,224.433Z"/><path d="M313.287,176.568a54.965,54.965,0,0,0,11.689,1.261c13.12,0,26.1-4.729,37.458-13.812,13.759-11,23.557-27.272,27.588-45.818,8.589-39.515-11.858-77.627-45.58-84.957-16.957-3.686-34.412.77-49.147,12.551-13.76,11-23.558,27.272-27.588,45.817C259.117,131.125,279.564,169.237,313.287,176.568Zm-14.31-78.16h0c2.474-11.387,8.263-21.2,16.3-27.621,5.572-4.454,11.689-6.781,17.637-6.781a22.258,22.258,0,0,1,4.732.5c16.2,3.522,25.866,25,21.107,46.89-2.476,11.387-8.265,21.2-16.3,27.622-7.061,5.646-15,7.874-22.367,6.275C303.883,141.776,294.217,120.3,298.977,98.408Z"/></svg>
                      </div>
                    </div>
                    <p style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Pacientes</p>
                    <p style={{ margin: 0, fontSize: '32px', fontWeight: '800', color: '#111', lineHeight: 1 }}>
                      {(clinicIndicators?.overview?.totalPatients ?? 0).toLocaleString('pt-BR')}
                    </p>
                  </div>
                </a>

                {/* Avaliação */}
                <a href="#" onClick={(e) => { e.preventDefault(); navigate('/reviews'); }} style={{ textDecoration: 'none' }}>
                  <div style={{ background: '#fff', borderRadius: '16px', padding: '20px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0', cursor: 'pointer', transition: 'box-shadow 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(239,68,68,0.12)'}
                    onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 6px rgba(0,0,0,0.06)'}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                      <div style={{ width: 42, height: 42, borderRadius: '12px', background: 'rgba(239,68,68,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef4444' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                      </div>
                    </div>
                    <p style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Avaliação</p>
                    <p style={{ margin: 0, fontSize: '32px', fontWeight: '800', color: '#111', lineHeight: 1 }}>
                      {(clinicIndicators?.overview?.avgReview ?? entity?.ratingScore ?? 0).toFixed(1)}<span style={{ fontSize: '16px', fontWeight: '600', color: '#9ca3af' }}>/5</span>
                    </p>
                  </div>
                </a>
              </div>

              {/* Two Column Layout */}
              <div className="content-grid">
                {/* Left Column */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  {/* Próximos Agendamentos */}
                  <section className="section-card">
                    <div className="section-header">
                      <h3 className="section-title">Próximos Agendamentos</h3>
                      <a href="#" className="section-link" onClick={(e) => { e.preventDefault(); navigate('/schedules'); }}>
                        Ver todos
                      </a>
                    </div>

                    {upcomingV3.length === 0 ? (
                      <div style={{ padding: '24px', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
                        Nenhum agendamento para hoje
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {upcomingV3.map((s) => {
                          const STATUS_CONFIG = {
                            scheduled:   { color: '#2563EB' },
                            confirmed:   { color: '#16A34A' },
                            in_progress: { color: '#D97706' },
                            completed:   { color: '#7165FF' },
                            canceled:   { color: '#DC2626' },
                            no_show:     { color: '#6B7280' },
                            blocked:     { color: '#374151' },
                          };
                          const STATUS_LABEL = {
                            scheduled:   'Agendado',
                            confirmed:   'Confirmado',
                            in_progress: 'Em atendimento',
                            completed:   'Finalizado',
                            canceled:   'Cancelado',
                            no_show:     'Não compareceu',
                            blocked:     'Bloqueado',
                          };
                          const STATUS_BG = {
                            scheduled:   '#EFF6FF',
                            confirmed:   '#DCFCE7',
                            in_progress: '#FEF3C7',
                            completed:   'rgba(113,101,255,0.1)',
                            canceled:   '#FEE2E2',
                            no_show:     '#F3F4F6',
                            blocked:     '#F3F4F6',
                          };
                          const cfg = STATUS_CONFIG[s.status] || STATUS_CONFIG.scheduled;
                          const time = dayjs.utc(s.dateTime).format('HH:mm');
                          return (
                            <div key={s.id}
                              onClick={() => navigate('/schedules')}
                              style={{
                                display: 'flex', alignItems: 'center', gap: '12px',
                                padding: '12px 0', borderBottom: '1px solid #f3f4f6',
                                cursor: 'pointer',
                              }}
                              onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                            >
                              {/* Color bar */}
                              <div style={{ width: '3px', height: '40px', borderRadius: '4px', background: cfg.color, flexShrink: 0 }} />

                              {/* Time */}
                              <span style={{ fontSize: '14px', fontWeight: '800', color: cfg.color, minWidth: '44px', fontVariantNumeric: 'tabular-nums' }}>
                                {time}
                              </span>

                              {/* Pet photo */}
                              <div style={{ width: '36px', height: '36px', borderRadius: '50%', overflow: 'hidden', background: '#f3f4f6', flexShrink: 0 }}>
                                <img src={s.pet?.photo || placeholderImage} alt={s.pet?.name || 'Pet'}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  onError={e => { e.target.onerror = null; e.target.src = placeholderImage; }} />
                              </div>

                              {/* Info */}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#111', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {s.pet?.name || 'Pet não informado'}
                                  {s.owner?.name && <span style={{ fontWeight: '400', color: '#6b7280' }}> · {s.owner.name}</span>}
                                </p>
                                {s.service?.name && (
                                  <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#9ca3af' }}>{s.service.name}</p>
                                )}
                              </div>

                              {/* Status badge */}
                              <span style={{
                                fontSize: '11px', fontWeight: '700', whiteSpace: 'nowrap',
                                background: STATUS_BG[s.status] || '#F3F4F6',
                                color: cfg.color,
                                padding: '3px 10px', borderRadius: '20px',
                              }}>
                                {STATUS_LABEL[s.status] || s.status}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </section>

                  {/* Avaliações Recentes */}
                  <section className="section-card">
                    <div className="section-header">
                      <h3 className="section-title">Avaliações Recentes</h3>
                      <a
                        href="#"
                        className="section-link"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate('/reviews');
                        }}
                      >
                        Ver todos
                      </a>
                    </div>

                    {reviews && reviews.length > 0 ? (
                      <div className="appointments-list">
                        {reviews.slice(0, 3).map((review) => (
                          <div
                            key={review.id}
                            className="review-item"
                            style={{
                              background: '#ffffff',
                              border: '1px solid #e5e7eb',
                              borderRadius: '12px',
                              padding: '20px',
                              transition: 'all 0.2s',
                              cursor: 'pointer'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = '#7165FF';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(113, 101, 255, 0.1)';
                              e.currentTarget.style.transform = 'translateY(-2px)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = '#e5e7eb';
                              e.currentTarget.style.boxShadow = 'none';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                          >
                            <img
                              src={review.user?.image || 'https://via.placeholder.com/40'}
                              alt={review.user?.name}
                              className="review-avatar"
                            />
                            <div className="review-content">
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <h4 className="review-name">{review.user?.name}</h4>
                                  {review.schedule?.dateTime && (
                                    <span style={{ fontSize: '13px', color: '#6b7280', fontStyle: 'italic' }}>
                                      Atendimento realizado em {dayjs(review.schedule.dateTime).format('MMMM [de] YYYY')}
                                    </span>
                                  )}
                                </div>
                                <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                  {dayjs(review.createdAt).fromNow()}
                                </span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                <div className="review-stars" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  {[...Array(5)].map((_, i) => {
                                    const fillPercentage = Math.min(Math.max(review.score - i, 0), 1) * 100;
                                    const starId = `star-${i}-${review.id}`;
                                    return (
                                      <svg
                                        key={i}
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        stroke="#F59E0B"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        style={{ display: 'block', position: 'relative' }}
                                      >
                                        <defs>
                                          <linearGradient id={starId}>
                                            <stop offset={`${fillPercentage}%`} stopColor="#F59E0B" />
                                            <stop offset={`${fillPercentage}%`} stopColor="transparent" />
                                          </linearGradient>
                                        </defs>
                                        <polygon
                                          points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
                                          fill={`url(#${starId})`}
                                        />
                                      </svg>
                                    );
                                  })}
                                </div>
                                {/* <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151', height: '16px' }}>
                              {review.score.toFixed(1)}
                            </span> */}
                              </div>
                              <p className="review-text">{review.content}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 12px', opacity: 0.5 }}>
                          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                        </svg>
                        <p style={{ margin: '8px 0 4px', fontWeight: '500' }}>Ainda não há avaliações</p>
                        <p style={{ fontSize: '14px', opacity: 0.7 }}>As avaliações dos clientes aparecerão aqui</p>
                      </div>
                    )}
                  </section>
                </div>

                {/* Right Column */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

                  {/* Próximos Retornos — v3 */}
                  {clinicIndicators?.upcomingReturns?.length > 0 && (
                    <section className="section-card">
                      <div className="section-header">
                        <h3 className="section-title">Próximos Retornos</h3>
                        <span style={{ background: 'var(--color-main-light)', color: 'var(--color-main)', fontSize: '12px', fontWeight: '700', padding: '3px 10px', borderRadius: '20px' }}>
                          {clinicIndicators.upcomingReturns.length}
                        </span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {clinicIndicators.upcomingReturns.map((ret, i) => (
                          <div key={ret.id || i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', borderBottom: '1px solid var(--color-border)' }}>
                            {ret.petPhoto ? (
                              <img src={ret.petPhoto} alt={ret.petName} style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} onError={(e) => { e.target.src = placeholderImage; }} />
                            ) : (
                              <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--color-main-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-main)" strokeWidth="2"><circle cx="12" cy="8" r="5" /><path d="M12 14c-5 0-8 2.5-8 4v2h16v-2c0-1.5-3-4-8-4z" /></svg>
                              </div>
                            )}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <p style={{ fontSize: '14px', fontWeight: '600', color: 'var(--color-text-primary)', margin: '0 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ret.petName || '—'}</p>
                              <p style={{ fontSize: '12px', color: 'var(--color-text-secondary)', margin: 0 }}>{ret.ownerName || ''}</p>
                              </div> 
                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                              {ret.returnDeadline && <p style={{ fontSize: '12px', fontWeight: '600', color: 'var(--color-text-secondary)', margin: '0 0 3px' }}>{dayjs(ret.returnDeadline).format('DD/MM/YYYY')}</p>}
                              {ret.returnPriority && (
                                <span style={{
                                  fontSize: '10px', fontWeight: '700', padding: '2px 7px', borderRadius: '10px',
                                  background: ret.returnPriority === 'high' ? '#FEE2E2' : ret.returnPriority === 'medium' ? '#FEF3C7' : '#F3F4F6',
                                  color: ret.returnPriority === 'high' ? '#DC2626' : ret.returnPriority === 'medium' ? '#D97706' : '#6B7280',
                                }}>
                                  {ret.returnPriority === 'high' ? 'Urgente' : ret.returnPriority === 'medium' ? 'Normal' : 'Baixa'}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}

                  {(() => {
                    const planStyles = {
                      'alzan.vet.premium': {
                        name: 'Premium',
                        gradient: 'linear-gradient(135deg, #7165FF 0%, #6D28D9 100%)',
                        icon: (
                          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="white" fillOpacity="0.2" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M12 2L15.09 8.26L22 9.27" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M12 2L8.91 8.26L2 9.27" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        ),
                        features: ['Exposição máxima na plataforma', 'Sistema de agendamentos completo', 'Selo de destaque exclusivo', 'Gestão de até 100 tutores e 500 pacientes']
                      },
                      'alzan.vet.plus': {
                        name: 'Plus',
                        gradient: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                        icon: (
                          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" fill="white" fillOpacity="0.2" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        ),
                        features: ['Exposição alta na plataforma', 'Sistema de agendamentos completo', 'Gestão de até 50 tutores e 150 pacientes']
                      },
                      'alzan.vet.standard': {
                        name: 'Básico',
                        gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                        icon: (
                          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="3" y="14" width="4" height="7" rx="1" fill="white" fillOpacity="0.2" stroke="white" strokeWidth="1.5" />
                            <rect x="10" y="8" width="4" height="13" rx="1" fill="white" fillOpacity="0.2" stroke="white" strokeWidth="1.5" />
                            <rect x="17" y="3" width="4" height="18" rx="1" fill="white" fillOpacity="0.2" stroke="white" strokeWidth="1.5" />
                          </svg>
                        ),
                        features: ['Exposição média na plataforma', 'Gestão de até 15 tutores e 50 pacientes']
                      },
                      'alzan.vet.free': {
                        name: 'Gratuito',
                        gradient: 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)',
                        icon: (
                          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="white" fillOpacity="0.2" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        ),
                        features: ['Exposição básica na plataforma', 'Gestão de até 3 tutores e 15 pacientes']
                      }
                    };

                    const currentPlanId = entity?.plan?.planId;
                    const isActive = entity?.plan?.active;
                    const isFreePlanExpired = !isActive && currentPlanId === 'alzan.vet.free';
                    const planStyle = (currentPlanId && isActive && planStyles[currentPlanId]) || (isFreePlanExpired ? {
                      name: 'Gratuito Encerrado',
                      gradient: 'linear-gradient(135deg, #9CA3AF 0%, #6B7280 100%)',
                      icon: (
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                          <line x1="8" y1="8" x2="16" y2="16" />
                          <line x1="16" y1="8" x2="8" y2="16" />
                        </svg>
                      ),
                      features: ['Seu período gratuito encerrou', 'Assine um plano para reativar seu negócio']
                    } : {
                      name: 'Sem plano',
                      gradient: 'linear-gradient(135deg, #E5E5E5 0%, #CCCCCC 100%)',
                      icon: (
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#666666" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                          <path d="M12 6v6l4 2" />
                        </svg>
                      ),
                      features: ['Assine para habilitar seu negócio']
                    });

                    return (
                      <div className="profile-completion-card" style={{
                        background: entity?.plan?.active ? planStyle.gradient : 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
                        border: entity?.plan?.active ? 'none' : '2px dashed #d1d5db',
                        position: 'relative',
                        overflow: 'hidden',
                        padding: '24px'
                      }}>
                        {entity?.plan?.active && (
                          <>
                            <div style={{
                              position: 'absolute',
                              top: '-30px',
                              right: '-30px',
                              width: '120px',
                              height: '120px',
                              background: 'rgba(255, 255, 255, 0.15)',
                              borderRadius: '50%',
                              filter: 'blur(30px)'
                            }}></div>
                            <div style={{
                              position: 'absolute',
                              bottom: '-40px',
                              left: '-40px',
                              width: '150px',
                              height: '150px',
                              background: 'rgba(255, 255, 255, 0.1)',
                              borderRadius: '50%',
                              filter: 'blur(40px)'
                            }}></div>
                          </>
                        )}

                        <div style={{ position: 'relative', zIndex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px' }}>
                            <div style={{
                              background: entity?.plan?.active ? 'rgba(255, 255, 255, 0.2)' : '#fff',
                              backdropFilter: 'blur(10px)',
                              padding: '12px',
                              borderRadius: '16px',
                              border: entity?.plan?.active ? '2px solid rgba(255, 255, 255, 0.3)' : '2px solid #e5e7eb'
                            }}>
                              {planStyle.icon}
                            </div>

                            <span style={{
                              background: entity?.plan?.active ? 'rgba(255, 255, 255, 0.25)' : '#fff',
                              backdropFilter: 'blur(10px)',
                              padding: '6px 14px',
                              borderRadius: '20px',
                              fontSize: '11px',
                              fontWeight: '700',
                              color: entity?.plan?.active ? '#fff' : '#6b7280',
                              letterSpacing: '0.5px',
                              border: entity?.plan?.active ? '2px solid rgba(255, 255, 255, 0.3)' : '2px solid #e5e7eb',
                              textTransform: 'uppercase'
                            }}>
                              {entity?.plan?.active ? 'Ativo' : 'Inativo'}
                            </span>
                          </div>

                          <h3 className="card-title" style={{
                            color: entity?.plan?.active ? '#fff' : '#1f2937',
                            fontSize: '24px',
                            fontWeight: '700',
                            marginBottom: '6px',
                            letterSpacing: '-0.5px'
                          }}>
                            {planStyle.name === 'Sem plano' ? 'Sem plano' : isFreePlanExpired ? 'Plano Gratuito Encerrado' : `Plano ${planStyle.name}`}
                          </h3>

                          <p className="card-subtitle" style={{
                            color: entity?.plan?.active ? 'rgba(255, 255, 255, 0.85)' : '#6b7280',
                            fontSize: '13px',
                            fontWeight: '500',
                            marginBottom: '16px',
                            lineHeight: '1.4'
                          }}>
                            {isFreePlanExpired ? 'Assine um plano para continuar visível' : entity?.plan?.active && currentPlanId !== 'alzan.vet.free' && currentPlanId !== 'alzan.vet.standard' ? 'Acesso completo a todas as funcionalidades' : 'Recursos limitados'}
                          </p>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                            {planStyle.features.map((feature, index) => (
                              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={entity?.plan?.active ? '#fff' : '#6b7280'} strokeWidth="2.5">
                                  <polyline points="20 6 9 17 4 12" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <span style={{
                                  color: entity?.plan?.active ? 'rgba(255, 255, 255, 0.9)' : '#4b5563',
                                  fontSize: '13px',
                                  fontWeight: '500'
                                }}>
                                  {feature}
                                </span>
                              </div>
                            ))}
                          </div>

                          {/* Quotas */}
                          {clinicIndicators?.overview?.plan && (
                            <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: `1px solid ${entity?.plan?.active ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.08)'}` }}>
                              {[
                                { label: 'Tutores', current: clinicIndicators.overview.plan.owners.current, limit: clinicIndicators.overview.plan.owners.limit },
                                { label: 'Pacientes', current: clinicIndicators.overview.plan.pets.current, limit: clinicIndicators.overview.plan.pets.limit }
                              ].map(quota => {
                                if (quota.limit === null || quota.limit === undefined) return null;
                                const percentage = quota.limit > 0 ? (quota.current / quota.limit) * 100 : 0;
                                let barColor = 'var(--color-main)';
                                if (entity?.plan?.active) {
                                  barColor = '#fff';
                                }
                                if (percentage > 85) barColor = '#ef4444'; // red
                                else if (percentage > 60) barColor = '#f59e0b'; // amber

                                return (
                                  <div key={quota.label} style={{ marginBottom: '12px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '5px' }}>
                                      <span style={{ fontSize: '13px', fontWeight: '600', color: entity?.plan?.active ? '#fff' : '#374151' }}>{quota.label}</span>
                                      <span style={{ fontSize: '12px', fontWeight: '700', color: entity?.plan?.active ? 'rgba(255,255,255,0.8)' : '#6b7280' }}>
                                        {quota.current} / {quota.limit}
                                      </span>
                                    </div>
                                    <div style={{ height: '8px', background: entity?.plan?.active ? 'rgba(0,0,0,0.2)' : '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                                      <div style={{
                                        height: '100%',
                                        width: `${percentage}%`,
                                        background: barColor,
                                        borderRadius: '4px',
                                        transition: 'width 0.5s ease-in-out, background-color 0.5s ease'
                                      }} />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {entity?.plan?.active && entity?.plan?.updatedAt && currentPlanId !== 'alzan.vet.free' && (
                            <p style={{
                              color: 'rgba(255, 255, 255, 0.75)',
                              fontSize: '12px',
                              fontWeight: '500',
                              marginTop: '12px',
                              paddingTop: '12px',
                              borderTop: '1px solid rgba(255, 255, 255, 0.2)'
                            }}>
                              Renovação automática em {new Date(entity.plan.updatedAt).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                            </p>
                          )}
                          {entity?.plan?.active && currentPlanId === 'alzan.vet.free' && entity?.plan?.expireDate && (() => {
                            const now = dayjs();
                            const expire = dayjs(entity.plan.expireDate);
                            const diffHours = expire.diff(now, 'hour');
                            const diffDays = expire.diff(now, 'day');
                            let expiryLabel = '';
                            if (diffHours <= 0) {
                              expiryLabel = 'Seu plano gratuito expirou';
                            } else if (diffHours < 24) {
                              expiryLabel = `Seu plano gratuito encerra em ${diffHours}h`;
                            } else {
                              expiryLabel = `Seu plano gratuito encerra em ${diffDays} ${diffDays === 1 ? 'dia' : 'dias'}`;
                            }
                            return (
                              <div style={{
                                marginTop: '12px',
                                paddingTop: '12px',
                                borderTop: '1px solid rgba(255, 255, 255, 0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="2">
                                  <circle cx="12" cy="12" r="10" />
                                  <polyline points="12 6 12 12 16 14" />
                                </svg>
                                <span style={{
                                  color: diffHours <= 24 ? '#FDE68A' : 'rgba(255,255,255,0.8)',
                                  fontSize: '12px',
                                  fontWeight: '600'
                                }}>
                                  {expiryLabel}
                                </span>
                              </div>
                            );
                          })()}

                          {entity?.plan?.active && currentPlanId === 'alzan.vet.free' && (
                            <button
                              onClick={() => setShowSubscription(true)}
                              className="btn-complete"
                              style={{
                                width: '100%',
                                padding: '12px',
                                background: 'rgba(255, 255, 255, 0.25)',
                                backdropFilter: 'blur(10px)',
                                color: '#fff',
                                border: '2px solid rgba(255, 255, 255, 0.3)',
                                borderRadius: '12px',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                marginTop: '20px',
                                transition: 'all 0.2s',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.35)';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                                e.currentTarget.style.transform = 'translateY(0)';
                              }}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                                <polyline points="17 6 23 6 23 12" />
                              </svg>
                              Melhorar meu plano
                            </button>
                          )}

                          {entity?.plan?.active && entity?.plan?.platform === 'stripe' && currentPlanId !== 'alzan.vet.free' && (
                            <button
                              onClick={() => window.open('https://billing.stripe.com/p/login/aFa9ATaGcd78gQsgwf5wI00', '_blank')}
                              className="btn-complete"
                              style={{
                                width: '100%',
                                padding: '12px',
                                background: 'rgba(255, 255, 255, 0.25)',
                                backdropFilter: 'blur(10px)',
                                color: '#fff',
                                border: '2px solid rgba(255, 255, 255, 0.3)',
                                borderRadius: '12px',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                marginTop: '20px',
                                transition: 'all 0.2s',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.35)';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                                e.currentTarget.style.transform = 'translateY(0)';
                              }}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="4" y1="21" x2="4" y2="14" />
                                <line x1="4" y1="10" x2="4" y2="3" />
                                <line x1="12" y1="21" x2="12" y2="12" />
                                <line x1="12" y1="8" x2="12" y2="3" />
                                <line x1="20" y1="21" x2="20" y2="16" />
                                <line x1="20" y1="12" x2="20" y2="3" />
                                <line x1="1" y1="14" x2="7" y2="14" />
                                <line x1="9" y1="8" x2="15" y2="8" />
                                <line x1="17" y1="16" x2="23" y2="16" />
                              </svg>
                              Gerenciar Plano
                            </button>
                          )}

                          {!entity?.plan?.active && (
                            <button
                              onClick={() => setShowSubscription(true)}
                              className="btn-complete"
                              style={{
                                width: '100%',
                                padding: '12px',
                                background: 'linear-gradient(135deg, #7165FF 0%, #6D28D9 100%)',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '12px',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                marginTop: '12px',
                                transition: 'transform 0.2s'
                              }}
                            >
                              Assinar Agora
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Sobre */}
                  {entity?.description && (
                    <section className="section-card">
                      <div className="card-header">
                        <h3 className="section-title">Descrição</h3>
                        <button
                          className="edit-btn"
                          title="Editar informações"
                          onClick={() => setShowEditBasicDataModal(true)}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                      </div>
                      <p style={{
                        fontSize: '15px',
                        lineHeight: '1.7',
                        color: '#4b5563',
                        margin: '0',
                        whiteSpace: 'pre-wrap'
                      }}>
                        {entity.description}
                      </p>
                    </section>
                  )}
                </div>
              </div>

              {/* Fotos da Clínica - Seção Ampla */}
              <section className="photos-section-wide">
                <div className="photos-card">
                  <div className="card-header">
                    <div>
                      <h3 className="card-title">Fotos</h3>
                      <p className="card-subtitle">Mostre seu espaço e atraia mais clientes</p>
                    </div>
                    {/* <button 
                  className="add-photo-btn-header"
                  onClick={handleAddPhotoClick}
                  disabled={uploadingPhoto || !(entity?.images || []).some(img => !img.url)}
                  title={!(entity?.images || []).some(img => !img.url) ? 'Todas as posições estão ocupadas' : 'Adicionar fotos'}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  Adicionar Fotos
                </button> */}
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />

                  <div className="photos-grid">
                    {(entity?.images || Array(5).fill(null).map((_, i) => ({ url: null, order: i }))).map((image, index) => (
                      image.url ? (
                        <div key={index} className="photo-item">
                          {index === 0 && <span className="photo-badge">Principal</span>}
                          <img
                            src={image.url}
                            alt={`Foto ${index + 1}`}
                            className="photo-image"
                            onClick={() => handleAddPhotoClick(index)}
                            style={{ cursor: 'pointer' }}
                            title="Clique para substituir esta foto"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = placeholderImage;
                            }}
                          />
                          <button
                            className="delete-photo-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeletePhoto(index);
                            }}
                            title="Excluir foto"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                              <line x1="10" y1="11" x2="10" y2="17" />
                              <line x1="14" y1="11" x2="14" y2="17" />
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <div
                          key={index}
                          className="photo-placeholder"
                          onClick={() => handleAddPhotoClick(index)}
                        >
                          <div className="photo-placeholder-content">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <line x1="12" y1="5" x2="12" y2="19" />
                              <line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                            <span>Adicionar foto</span>
                          </div>
                        </div>
                      )
                    ))}
                  </div>

                  {(entity?.images || []).some(img => img.url) && (
                    <div className="photos-info">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="16" x2="12" y2="12" />
                        <line x1="12" y1="8" x2="12.01" y2="8" />
                      </svg>
                      <span>{(entity?.images || []).filter(img => img.url).length}/5 fotos • A primeira foto será a principal</span>
                    </div>
                  )}
                </div>
              </section>

              {/* Dados Básicos - Horizontal */}
              <section className="section-card" style={{ marginTop: '24px' }}>
                <div className="card-header">
                  <h3 className="section-title">Dados Básicos</h3>
                  <button
                    className="edit-btn"
                    title="Editar informações"
                    onClick={() => setShowEditBasicDataModal(true)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
                  <div className="info-item-modern">
                    <div className="info-icon-wrapper info-icon-blue">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                      </svg>
                    </div>
                    <div className="info-content">
                      <span className="info-label-modern">Telefone</span>
                      <span className="info-value-modern">{formatPhone(entity?.phone)}</span>
                    </div>
                  </div>

                  <div className="info-item-modern">
                    <div className="info-icon-wrapper info-icon-purple">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                    </div>
                    <div className="info-content">
                      <span className="info-label-modern">Endereço</span>
                      <span className="info-value-modern">
                        {entity?.address}, {entity?.addressNumber}
                        {entity?.addressComplement && ` - ${entity.addressComplement}`}
                      </span>
                    </div>
                  </div>

                  <div className="info-item-modern">
                    <div className="info-icon-wrapper info-icon-orange">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="10" r="3" />
                        <path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 1 0-16 0c0 3 2.7 6.9 8 11.7z" />
                      </svg>
                    </div>
                    <div className="info-content">
                      <span className="info-label-modern">Cidade</span>
                      <span className="info-value-modern">{entity?.addressCity} - {entity?.addressUF}</span>
                    </div>
                  </div>

                  <div className="info-item-modern">
                    <div className="info-icon-wrapper info-icon-red">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
                      </svg>
                    </div>
                    <div className="info-content">
                      <span className="info-label-modern">CEP</span>
                      <span className="info-value-modern">{entity?.addressZIP || 'Não informado'}</span>
                    </div>
                  </div>

                  <div className="info-item-modern">
                    <div className="info-icon-wrapper info-icon-gray">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                        <polyline points="10 9 9 9 8 9" />
                      </svg>
                    </div>
                    <div className="info-content">
                      <span className="info-label-modern">{entity?.documentType || 'Documento'}</span>
                      <span className="info-value-modern">{entity?.documentNumber || 'Não informado'}</span>
                    </div>
                  </div>

                  <div className="info-item-modern">
                    <div className="info-icon-wrapper info-icon-green">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                    </div>
                    <div className="info-content">
                      <span className="info-label-modern">Horário de Funcionamento</span>
                      <span className="info-value-modern">{entity?.openingHours || 'Não informado'}</span>
                    </div>
                  </div>

                  {/* <div className="info-item-modern">
                    <div className="info-icon-wrapper" style={{ background: entity?.verifiedIdentity ? 'rgba(16, 185, 129, 0.1)' : 'rgba(107, 114, 128, 0.1)' }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={entity?.verifiedIdentity ? '#10B981' : '#6b7280'} strokeWidth="2">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                        {entity?.verifiedIdentity && <polyline points="9 12 11 14 15 10" strokeLinecap="round" strokeLinejoin="round" />}
                      </svg>
                    </div>
                    <div className="info-content">
                      <span className="info-label-modern">Identidade Verificada</span>
                      <span className="info-value-modern" style={{ color: entity?.verifiedIdentity ? '#10B981' : '#6b7280', fontWeight: entity?.verifiedIdentity ? '600' : '400' }}>
                        {entity?.verifiedIdentity ? 'Verificado' : 'Não verificado'}
                      </span>
                    </div>
                  </div> */}
                </div>
              </section>
            </>
          ) : currentView === 'reviews' ? (
            /* Reviews Content */
            <>
              <div className="welcome-section">
                <div>
                  <h2 className="welcome-title">Avaliações</h2>
                  <p className="welcome-subtitle">Veja o que seus clientes estão dizendo</p>
                </div>
              </div>

              {/* Overview Card */}
              <div className="reviews-overview" style={{ marginBottom: '32px' }}>
                <div className="overview-card" style={{
                  background: 'linear-gradient(135deg, #7165FF 0%, #6D28D9 100%)',
                  borderRadius: '20px',
                  padding: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '32px',
                  boxShadow: '0 10px 40px rgba(113, 101, 255, 0.3)',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: '-50px',
                    right: '-50px',
                    width: '200px',
                    height: '200px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '50%',
                    filter: 'blur(40px)'
                  }}></div>
                  <div style={{
                    position: 'absolute',
                    bottom: '-80px',
                    left: '-80px',
                    width: '250px',
                    height: '250px',
                    background: 'rgba(255, 255, 255, 0.08)',
                    borderRadius: '50%',
                    filter: 'blur(60px)'
                  }}></div>

                  <div style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)',
                    padding: '20px',
                    borderRadius: '20px',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    position: 'relative',
                    zIndex: 1
                  }}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                  </div>

                  <div style={{ flex: 1, position: 'relative', zIndex: 1 }}>
                    <h3 style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: 'rgba(255, 255, 255, 0.9)',
                      margin: '0 0 16px 0',
                      letterSpacing: '-0.3px'
                    }}>Avaliação Geral</h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
                      <span style={{
                        fontSize: '56px',
                        fontWeight: '700',
                        color: '#ffffff',
                        lineHeight: 1,
                        letterSpacing: '-2px'
                      }}>{entity?.ratingScore?.toFixed(1) || '0.0'}</span>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        {[...Array(5)].map((_, i) => {
                          const score = entity?.ratingScore || 0;
                          const fillPercentage = Math.min(Math.max(score - i, 0), 1) * 100;
                          const starId = `star-overview-${i}`;
                          return (
                            <svg
                              key={i}
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              stroke="#FFB800"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <defs>
                                <linearGradient id={starId}>
                                  <stop offset={`${fillPercentage}%`} stopColor="#FFB800" />
                                  <stop offset={`${fillPercentage}%`} stopColor="transparent" />
                                </linearGradient>
                              </defs>
                              <polygon
                                points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
                                fill={`url(#${starId})`}
                              />
                            </svg>
                          );
                        })}
                      </div>
                    </div>
                    <p style={{
                      fontSize: '15px',
                      color: 'rgba(255, 255, 255, 0.85)',
                      margin: 0,
                      fontWeight: '500'
                    }}>
                      Baseado em {entity?.ratingCount || 0} {entity?.ratingCount === 1 ? 'avaliação' : 'avaliações'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Reviews List */}
              <div className="section-card">
                <div className="section-header" style={{ marginBottom: '24px' }}>
                  <h3 className="section-title">Todas as Avaliações</h3>
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#6b7280',
                    background: '#f3f4f6',
                    padding: '6px 12px',
                    borderRadius: '20px'
                  }}>{reviews.length} {reviews.length === 1 ? 'avaliação' : 'avaliações'}</span>
                </div>

                {reviews.length === 0 ? (
                  <div style={{ padding: '80px 24px', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 24px', opacity: 0.3 }}>
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                    <p style={{ margin: '8px 0 4px', fontWeight: '500' }}>Ainda não há avaliações</p>
                    <p style={{ fontSize: '14px', opacity: 0.7 }}>As avaliações dos seus clientes aparecerão aqui</p>
                  </div>
                ) : (
                  <div className="appointments-list">
                    {reviews.map((review) => (
                      <div
                        key={review.id}
                        className="review-item"
                        style={{
                          background: '#ffffff',
                          border: '1px solid #e5e7eb',
                          borderRadius: '12px',
                          padding: '20px',
                          transition: 'all 0.2s',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = '#7165FF';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(113, 101, 255, 0.1)';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = '#e5e7eb';
                          e.currentTarget.style.boxShadow = 'none';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        <img
                          src={review.user?.image || 'https://via.placeholder.com/40'}
                          alt={review.user?.name}
                          className="review-avatar"
                        />
                        <div className="review-content">
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <h4 className="review-name">{review.user?.name}</h4>
                              {review.schedule?.dateTime && (
                                <span style={{ fontSize: '13px', color: '#6b7280', fontStyle: 'italic' }}>
                                  Atendimento realizado em {dayjs(review.schedule.dateTime).format('MMMM [de] YYYY')}
                                </span>
                              )}
                            </div>
                            <span style={{ fontSize: '12px', color: '#6b7280' }}>
                              {dayjs(review.createdAt).fromNow()}
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <div className="review-stars" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              {[...Array(5)].map((_, i) => {
                                const fillPercentage = Math.min(Math.max(review.score - i, 0), 1) * 100;
                                const starId = `star-all-${i}-${review.id}`;
                                return (
                                  <svg
                                    key={i}
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    stroke="#F59E0B"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    style={{ display: 'block', position: 'relative' }}
                                  >
                                    <defs>
                                      <linearGradient id={starId}>
                                        <stop offset={`${fillPercentage}%`} stopColor="#F59E0B" />
                                        <stop offset={`${fillPercentage}%`} stopColor="transparent" />
                                      </linearGradient>
                                    </defs>
                                    <polygon
                                      points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
                                      fill={`url(#${starId})`}
                                    />
                                  </svg>
                                );
                              })}
                            </div>
                            {/* <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151', lineHeight: '1' }}>
                              {review.score.toFixed(1)}
                            </span> */}
                          </div>
                          <p className="review-text">{review.content}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : currentView === 'settings' ? (
            /* Settings Content */
            <Settings activeTab={settingsTab} />
          ) : currentView === 'patients' ? (
            <Pacientes onNavigateToPet={(pet) => {
              navigate(`/medical-record/pet/${pet.id}`, { state: { pet } });
              setCurrentView('medical-record');
            }} />
          ) : currentView === 'schedules' ? (
            /* Schedules Content */
            <Schedules onStartConsultation={(schedule) => {
              setPendingConsultationSchedule(schedule);
              setCurrentView('medical-record');
            }} onViewConsultation={(schedule) => {
              setPendingViewSchedule(schedule);
              setCurrentView('medical-record');
            }} />
          ) : currentView === 'appointments' ? (
            /* Appointments Content */
            <Consultations />
          ) : currentView === 'notifications' ? (
            /* Notifications Content */
            <Notifications
              notifications={notifications}
              onNotificationsUpdate={setNotifications}
            />
          ) : currentView === 'medical-record' ? (
            /* Medical Record Content */
            <MedicalRecord
              scheduleRef={pendingConsultationSchedule}
              onScheduleRefConsumed={() => setPendingConsultationSchedule(null)}
              viewScheduleRef={pendingViewSchedule}
              onViewScheduleRefConsumed={() => setPendingViewSchedule(null)}
            />
          ) : null}


        </div>
      </main>

      {/* Modal de Assinatura */}
      {showSubscription && (
        <Subscription
          onClose={() => setShowSubscription(false)}
          onSubscribe={handleSubscribe}
        />
      )}

      {/* Modal de Requisitos */}
      {showRequirementsModal && (
        <div className="modal-overlay" onClick={() => setShowRequirementsModal(false)}>
          <div className="requirements-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-button" onClick={() => setShowRequirementsModal(false)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            <div className="requirements-header">
              <div className="requirements-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 11l3 3L22 4" />
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                </svg>
              </div>
              <h3 className="requirements-title">Requisitos Obrigatórios</h3>
              <p className="requirements-subtitle">
                Complete {completedRequirements} de {totalRequirements} itens obrigatórios
              </p>
              <div className="requirements-progress">
                <div
                  className="requirements-progress-bar"
                  style={{ width: `${(completedRequirements / totalRequirements) * 100}%` }}
                ></div>
              </div>
            </div>

            <div className="requirements-list">
              <div className={`requirement-item ${hasBasicData ? 'completed' : 'pending'}`}>
                <div className="requirement-icon">
                  {hasBasicData ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="8" y1="12" x2="16" y2="12" />
                    </svg>
                  )}
                </div>
                <div className="requirement-content">
                  <h4 className="requirement-name">Dados Básicos</h4>
                  <p className="requirement-description">
                    {hasBasicData ? 'Informações básicas completas' : 'Complete telefone, endereço e cidade'}
                  </p>
                </div>
                {!hasBasicData && (
                  <button
                    className="requirement-action"
                    onClick={() => {
                      setShowRequirementsModal(false);
                      navigate('/settings');
                    }}
                  >
                    Completar
                  </button>
                )}
              </div>

              <div className={`requirement-item ${hasServices ? 'completed' : 'pending'}`}>
                <div className="requirement-icon">
                  {hasServices ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="8" y1="12" x2="16" y2="12" />
                    </svg>
                  )}
                </div>
                <div className="requirement-content">
                  <h4 className="requirement-name">Serviços</h4>
                  <p className="requirement-description">
                    {hasServices ? 'Pelo menos um serviço ativo' : 'Adicione e ative pelo menos um serviço'}
                  </p>
                </div>
                {!hasServices && (
                  <button
                    className="requirement-action"
                    onClick={() => {
                      setShowRequirementsModal(false);
                      navigate('/settings?tab=services');
                    }}
                  >
                    Adicionar
                  </button>
                )}
              </div>

              <div className={`requirement-item ${hasPlan ? 'completed' : 'pending'}`}>
                <div className="requirement-icon">
                  {hasPlan ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="8" y1="12" x2="16" y2="12" />
                    </svg>
                  )}
                </div>
                <div className="requirement-content">
                  <h4 className="requirement-name">Plano Ativo</h4>
                  <p className="requirement-description">
                    {hasPlan ? 'Plano ativo e funcionando' : 'Assine um plano para ativar recursos'}
                  </p>
                </div>
                {!hasPlan && (
                  <button
                    className="requirement-action"
                    onClick={() => {
                      setShowRequirementsModal(false);
                      setShowSubscription(true);
                    }}
                  >
                    Assinar
                  </button>
                )}
              </div>
            </div>

            {completedRequirements === totalRequirements && (
              <div className="requirements-success">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <span>Todos os requisitos foram completados!</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de Detalhes do Agendamento */}
      {showScheduleModal && selectedScheduleId && (
        <ScheduleDetailsModal
          scheduleId={selectedScheduleId}
          onClose={handleScheduleModalClose}
          onStatusChange={handleScheduleStatusChange}
        />
      )}

      {/* Modal de Edição de Dados Básicos */}
      <EditBasicDataModal
        entity={entity}
        isOpen={showEditBasicDataModal}
        onClose={() => setShowEditBasicDataModal(false)}
        onSave={handleSaveBasicData}
      />

      {/* In-Progress Widget */}
      {!inProgressDismissed && clinicIndicators?.inProgressAttendance && (
        <InProgressWidget
          schedule={clinicIndicators.inProgressAttendance}
          onClose={() => setInProgressDismissed(true)}
          onNavigate={(attendance) => {
            setInProgressDismissed(true);
            navigate(`/medical-record/pet/${attendance.pet.id}/consultation/${attendance.consultationId}`, {
              state: { pet: { ...attendance.pet, owner: attendance.owner } },
            });
            setCurrentView('medical-record');
          }}
        />
      )}
    </div>
  );
}

export default Dashboard;